/**
 * MODULE: domain/templates/frontend/api/savedViewsApi.ts
 * PURPOSE: HTTP client for Sales Hub saved library views (**`/api/v1/saved_views`**)
 * DEPENDENCIES: @shared/lib/frontend/api/config
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~75 / 200 lines
 * RECENT: 2026-04-19 — CAL-22 **`scope`** list or create (**`library`** \| **`calendar`**). 2026-04-18 — G5.
 */

import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';

const BASE = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/saved_views`;

export type SavedViewScope = 'library' | 'calendar';

export type SavedViewRow = {
  id: string;
  name: string;
  query_snapshot: string;
  scope?: SavedViewScope;
  pinned?: boolean;
  owner_user_id?: string;
  created_at?: string;
  updated_at?: string;
};

async function fetchJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...getAuthHeaders(), ...init.headers },
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error((err as { detail?: string }).detail || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const savedViewsApi = {
  list: async (scope: SavedViewScope = 'library'): Promise<SavedViewRow[]> => {
    const url = `${BASE}?scope=${encodeURIComponent(scope)}`;
    const out = await fetchJson<{ data: SavedViewRow[] }>(url);
    return out.data ?? [];
  },

  create: async (body: {
    name: string;
    query_snapshot: string;
    scope?: SavedViewScope;
  }): Promise<SavedViewRow> => {
    return fetchJson<SavedViewRow>(BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  patch: async (id: string, body: { pinned?: boolean; name?: string }): Promise<SavedViewRow> => {
    return fetchJson<SavedViewRow>(`${BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  remove: async (id: string): Promise<void> => {
    await fetchJson<void>(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};
